import { Modal, Button, Space } from 'antd';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Card as PrimeCard } from 'primereact/card';

import { Divider as PrimeDivider } from 'primereact/divider';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

const PREVIEW_RADIUS = 8;
const PREVIEW_BORDER_COLOR = '#c8ced8';
const PREVIEW_MUTED_TEXT_COLOR = '#6b7280';

const tileStyle = {
  border: `1px solid ${PREVIEW_BORDER_COLOR}`,
  borderRadius: PREVIEW_RADIUS,
  padding: '10px 12px',
  background: '#f8fafc',
};

const toRad = (value) => (value * Math.PI) / 180;

const getDistanceInMeters = (start, end) => {
  if (!start || !end) return null;
  const [lng1, lat1] = start;
  const [lng2, lat2] = end;
  if ([lng1, lat1, lng2, lat2].some((v) => typeof v !== 'number' || Number.isNaN(v))) return null;

  const earthRadius = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadius * c;
};

const formatMeters = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return 'N/A';
  if (value >= 1000) return `${(value / 1000).toFixed(2)} km`;
  return `${value.toFixed(1)} m`;
};

const getAssetLayerLabel = (asset) =>
  asset.layer_name ||
  asset.drawing_name ||
  asset.layer ||
  asset.drawing?.name ||
  asset.drawingName ||
  'Unassigned';

const isActualLayer = (layerLabel) => {
  if (typeof layerLabel !== 'string') return false;
  const normalized = layerLabel.trim().toLowerCase();
  return (
    normalized !== '' &&
    normalized !== 'unassigned' &&
    normalized !== 'none' &&
    normalized !== 'n/a'
  );
};

export default function SurveyPreviewModal({ previewSurvey, assetsLoading, onClose }) {
  const previewAssets = useMemo(() => {
    if (!previewSurvey || !Array.isArray(previewSurvey.members)) return [];

    const sortedMembers = [...previewSurvey.members].sort((a, b) => {
      const indexA = Number.isFinite(a.seq_index) ? a.seq_index : Number.MAX_SAFE_INTEGER;
      const indexB = Number.isFinite(b.seq_index) ? b.seq_index : Number.MAX_SAFE_INTEGER;
      if (indexA === indexB) return (a.asset_id || 0) - (b.asset_id || 0);
      return indexA - indexB;
    });
    const finiteSeqIndexes = sortedMembers
      .map((member) => member.seq_index)
      .filter((seq) => Number.isFinite(seq));
    const minSeqIndex = finiteSeqIndexes.length ? Math.min(...finiteSeqIndexes) : null;

    let cumulativeDistance = 0;

    return sortedMembers.map((member, index) => {
      const coordinates = [member.longitude, member.latitude];
      const previous = index > 0 ? sortedMembers[index - 1] : null;
      const previousCoordinates =
        previous && previous.longitude != null && previous.latitude != null
          ? [previous.longitude, previous.latitude]
          : null;
      const segmentDistance =
        index > 0 ? getDistanceInMeters(previousCoordinates, coordinates) : null;

      if (typeof segmentDistance === 'number') {
        cumulativeDistance += segmentDistance;
      }

      return {
        key: member.asset_id || member.id || index,
        order:
          Number.isFinite(member.seq_index) && member.seq_index !== null && minSeqIndex !== null
            ? member.seq_index - minSeqIndex + 1
            : index + 1,
        assetId: member.asset_id || member.id,
        assetName: member.name || member.asset_name || `Asset ${member.asset_id || member.id}`,
        layer: getAssetLayerLabel(member),
        layerColor: member.layer_color || member.drawing_color || null,
        latitude: member.latitude,
        longitude: member.longitude,
        segmentDistance,
        cumulativeDistance,
      };
    });
  }, [previewSurvey]);

  const distinctLayerCount = useMemo(() => {
    const seen = new Set();
    previewAssets.forEach((asset) => {
      if (isActualLayer(asset.layer)) seen.add(asset.layer);
    });
    return seen.size;
  }, [previewAssets]);

  const previewTotalDistance = previewAssets[previewAssets.length - 1]?.cumulativeDistance || 0;

  const topSummaryTiles = [
    { label: 'Created By', value: previewSurvey?.email || 'Unknown' },
    {
      label: 'Connections',
      value: previewSurvey?.edges?.length ?? Math.max(previewAssets.length - 1, 0),
    },
    { label: 'Actual Layers', value: distinctLayerCount },
  ];

  const secondSummaryTiles = [
    { label: 'Assets in Order', value: previewAssets.length },
    { label: 'Total Distance', value: formatMeters(previewTotalDistance) },
    { label: 'Connection Segments', value: Math.max(previewAssets.length - 1, 0) },
    { label: 'Distinct Layers', value: distinctLayerCount },
  ];

  return (
    <Modal
      open={!!previewSurvey}
      onCancel={onClose}
      footer={
        <Space>
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
      width={1100}
      title="Survey Preview"
      centered
      styles={{
        body: {
          maxHeight: 'calc(90vh - 120px)',
          overflow: 'hidden',
        },
        header: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 16,
          fontWeight: 600,
          fontSize: 18,
        },
        footer: {
          display: 'flex',
          justifyContent: 'end',
          gap: 16,
          alignItems: 'center',
          padding: 8,
        },
      }}
    >
      {previewSurvey && (
        <div
          style={{
            maxHeight: 'calc(90vh - 120px)',
            overflowY: 'auto',
            padding: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <PrimeCard>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, lineHeight: 1.3 }}>
                {previewSurvey.note || `Survey #${previewSurvey.id}`}
              </h3>
              <div style={{ color: PREVIEW_MUTED_TEXT_COLOR, marginTop: 4 }}>
                Created {dayjs(previewSurvey.created_at).format('DD MMM YYYY')}
              </div>
            </div>

            <PrimeDivider />

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
                gap: 10,
              }}
            >
              {topSummaryTiles.map((item) => (
                <div key={item.label} style={tileStyle}>
                  <div style={{ color: PREVIEW_MUTED_TEXT_COLOR, fontSize: 12 }}>{item.label}</div>
                  <div style={{ fontWeight: 600 }}>{item.value}</div>
                </div>
              ))}
            </div>
          </PrimeCard>

          <PrimeCard>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 10,
              }}
            >
              {secondSummaryTiles.map((item) => (
                <div key={item.label} style={tileStyle}>
                  <div style={{ color: PREVIEW_MUTED_TEXT_COLOR, fontSize: 12 }}>{item.label}</div>
                  <div style={{ fontWeight: 600 }}>{item.value}</div>
                </div>
              ))}
            </div>
          </PrimeCard>

          <PrimeCard>
            <h4 style={{ margin: '0 0 12px', fontSize: 16 }}>Assets</h4>
            <div
              style={{
                border: `1px solid ${PREVIEW_BORDER_COLOR}`,
                borderRadius: PREVIEW_RADIUS,
                overflow: 'hidden',
              }}
            >
              <DataTable
                value={previewAssets}
                dataKey="key"
                loading={assetsLoading}
                size="small"
                scrollable
                scrollHeight="280px"
                stripedRows
                emptyMessage="No assets found for this survey"
              >
                <Column field="order" header="#" style={{ width: 70 }} />
                <Column
                  header="Asset"
                  body={(row) => <span style={{ fontWeight: 600 }}>{row.assetName}</span>}
                />
                <Column
                  field="layer"
                  header="Layer"
                  body={(row) =>
                    isActualLayer(row.layer) ? (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 10px',
                          borderRadius: 4,
                          fontSize: 13,
                          border: `1.5px solid ${row.layerColor || PREVIEW_BORDER_COLOR}`,
                          color: row.layerColor || '#333',
                          background: 'transparent',
                        }}
                      >
                        {row.layer}
                      </span>
                    ) : (
                      <span style={{ color: PREVIEW_MUTED_TEXT_COLOR }}>-</span>
                    )
                  }
                />
                <Column
                  header="Coordinates"
                  body={(row) =>
                    row.longitude != null && row.latitude != null
                      ? `${row.longitude.toFixed(6)}, ${row.latitude.toFixed(6)}`
                      : 'N/A'
                  }
                />
                <Column
                  field="segmentDistance"
                  header="From Previous"
                  body={(row) => (row.order === 1 ? '-' : formatMeters(row.segmentDistance))}
                />
                <Column
                  field="cumulativeDistance"
                  header="Cumulative"
                  body={(row) => formatMeters(row.cumulativeDistance)}
                />
              </DataTable>
            </div>
          </PrimeCard>
        </div>
      )}
    </Modal>
  );
}
